import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fulfillmentLabel } from '../utils/fulfillment';
import { productService } from '../services/api';
import {
  PRODUCT_CATEGORIES,
  PACKAGE_TYPE_DIMENSIONS_CM,
  DEFAULT_STORE_CATEGORY,
  MAX_EXTRA_PRODUCT_TAXES
} from '../constants/storeCategories';
import './ProductManagement.css';

const emptyImages = () => ({
  front: '',
  back: '',
  left: '',
  right: '',
  diagonal: '',
  detail: '',
  video1: '',
  video2: ''
});

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [listStatusFilter, setListStatusFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const formPanelRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: DEFAULT_STORE_CATEGORY,
    season: '',
    launchDate: '',
    endDate: '',
    supplierCost: '',
    supplierShippingReference: '',
    price: '',
    totalQuantity: '',
    status: 'active',
    taxCategory: 'standard',
    ivaRate: 19,
    exemptReason: '',
    extraTaxes: [],
    fulfillment: 'javiland',
    weight: '500',
    lengthCm: '',
    widthCm: '',
    heightCm: '',
    packageType: 'sobre',
    usesSizeVariants: false,
    images: emptyImages(),
    variants: [
      { size: 'XS', stock: 0, price: 0 },
      { size: 'S', stock: 0, price: 0 },
      { size: 'M', stock: 0, price: 0 },
      { size: 'L', stock: 0, price: 0 },
      { size: 'XL', stock: 0, price: 0 }
    ]
  });

  // Temporadas disponibles
  const seasons = [
    'Verano 2024',
    'Otoño 2024', 
    'Invierno 2024',
    'Primavera 2025',
    'Verano 2025'
  ];

  // Estados del producto
  const fiscalSelectOptions = [
    { value: 'standard', label: 'IVA 19% estándar' },
    { value: 'reduced', label: 'IVA 5% reducido' },
    { value: 'exempt', label: 'Exento de IVA' },
    { value: 'consumption', label: 'Impuesto al consumo 8%' },
    { value: 'other', label: 'Otro (especificar nombre)' }
  ];

  const defaultRateForTax = (tc) => {
    switch (tc) {
      case 'standard':
        return 19;
      case 'reduced':
        return 5;
      case 'exempt':
        return 0;
      case 'consumption':
        return 8;
      default:
        return '';
    }
  };

  const addExtraTaxRow = () => {
    setFormData((prev) => {
      if (prev.extraTaxes.length >= MAX_EXTRA_PRODUCT_TAXES) return prev;
      const tc = 'consumption';
      return {
        ...prev,
        extraTaxes: [...prev.extraTaxes, { taxCategory: tc, rate: String(defaultRateForTax(tc)), customLabel: '' }]
      };
    });
  };

  const removeExtraTaxRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      extraTaxes: prev.extraTaxes.filter((_, i) => i !== index)
    }));
  };

  const updateExtraTaxRow = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.extraTaxes];
      const row = { ...next[index], [field]: value };
      if (field === 'taxCategory') {
        const dr = defaultRateForTax(value);
        row.rate = dr === '' ? '' : String(dr);
        if (value !== 'other') row.customLabel = '';
      }
      next[index] = row;
      return { ...prev, extraTaxes: next };
    });
  };

  const statusOptions = [
    { value: 'active', label: 'Activo', color: 'success' },
    { value: 'inactive', label: 'Inactivo', color: 'secondary' },
    { value: 'upcoming', label: 'Próximo Lanzamiento', color: 'warning' },
    { value: 'soldout', label: 'Agotado', color: 'danger' }
  ];

  const listFilterOptions = [
    { value: 'all', label: 'Todos los estados' },
    ...statusOptions.map((s) => ({ value: s.value, label: s.label }))
  ];

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listStatusFilter]);

  useEffect(() => {
    if (showAddForm && formPanelRef.current) {
      formPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showAddForm, editingProduct?.id]);

  const productPriority = (p) => {
    const f = p.fulfillment === 'supplier' || p.fulfillment === 'dropshipping' ? 'dropshipping' : 'javiland';
    const stock = Number(p.stock ?? p.totalQuantity ?? 0);
    if (f === 'javiland' && stock > 0) return 0;
    if (f === 'javiland') return 1;
    if (f === 'dropshipping') return 2;
    if (p.status === 'active') return 3;
    return 4;
  };

  const displayedProducts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = products.filter((p) => {
      if (!q) return true;
      return (
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.id).includes(q) ||
        String(p.category || '').toLowerCase().includes(q)
      );
    });
    return [...list].sort(
      (a, b) => productPriority(a) - productPriority(b) || String(a.name).localeCompare(String(b.name), 'es')
    );
  }, [products, searchTerm]);

  const fetchProducts = async () => {
    try {
      const res = await productService.getAllProducts({
        status: listStatusFilter,
        limit: 300
      });
      const productsList = res?.data?.products ?? res?.products ?? [];
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTaxCategoryChange = (e) => {
    const v = e.target.value;
    setFormData(prev => ({
      ...prev,
      taxCategory: v,
      ivaRate: v === 'exempt' ? 0 : v === 'reduced' ? 5 : v === 'consumption' ? 8 : 19,
      exemptReason: v === 'exempt' ? prev.exemptReason : ''
    }));
  };

  const applyStandardPackageDimensions = () => {
    const spec = PACKAGE_TYPE_DIMENSIONS_CM[formData.packageType] || PACKAGE_TYPE_DIMENSIONS_CM.sobre;
    setFormData(prev => ({
      ...prev,
      lengthCm: String(spec.l),
      widthCm: String(spec.w),
      heightCm: String(spec.h)
    }));
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    
    // Si cambia el precio, actualizar todas las variantes
    if (field === 'price') {
      newVariants.forEach(variant => {
        variant.price = value;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      variants: newVariants
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parseDim = (v) => {
      if (v === '' || v == null) return null;
      const n = parseFloat(String(v).replace(',', '.'));
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
    };
    const weightGrams = Math.max(1, parseInt(formData.weight, 10) || 500);
    const l = parseDim(formData.lengthCm);
    const w = parseDim(formData.widthCm);
    const h = parseDim(formData.heightCm);

    if (formData.fulfillment === 'javiland' && (l == null || w == null || h == null)) {
      alert('Para envío Javiland indica largo, ancho y alto del paquete en cm (obligatorio).');
      return;
    }

    if (formData.taxCategory === 'exempt' && !String(formData.exemptReason || '').trim()) {
      alert('Si la categoría fiscal es Exento de IVA, indica la razón de exención.');
      return;
    }

    const stock =
      formData.usesSizeVariants
        ? formData.variants.reduce((s, v) => s + (v.stock || 0), 0)
        : parseInt(formData.totalQuantity, 10) || 0;

    const priceVal = parseFloat(String(formData.price).replace(',', '.'));
    if (!Number.isFinite(priceVal) || priceVal < 0) {
      alert('Indica un precio de venta válido al público.');
      return;
    }

    const media = {
      back: formData.images.back || '',
      left: formData.images.left || '',
      right: formData.images.right || '',
      diagonal: formData.images.diagonal || '',
      detail: formData.images.detail || '',
      video1: formData.images.video1 || '',
      video2: formData.images.video2 || ''
    };

    const payload = {
      name: formData.name,
      description: formData.description,
      price: priceVal,
      supplierCost:
        formData.supplierCost === '' || formData.supplierCost == null
          ? null
          : Math.max(0, parseFloat(String(formData.supplierCost).replace(',', '.')) || 0),
      supplierShippingReferenceCop:
        formData.fulfillment === 'supplier' &&
        formData.supplierShippingReference !== '' &&
        formData.supplierShippingReference != null
          ? Math.max(0, parseFloat(String(formData.supplierShippingReference).replace(',', '.')) || 0)
          : null,
      category: formData.category || DEFAULT_STORE_CATEGORY,
      season: formData.season || '',
      launchDate: formData.launchDate || '',
      endDate: formData.endDate || '',
      image_url: formData.images?.front || '',
      stock,
      status: formData.status,
      active: formData.status === 'active' || formData.status === 'soldout' ? 1 : 0,
      brand: formData.brand || '',
      fulfillment: formData.fulfillment === 'supplier' ? 'supplier' : 'javiland',
      weightGrams,
      lengthCm: l,
      widthCm: w,
      heightCm: h,
      packageType: formData.packageType,
      extraTaxes: formData.extraTaxes
        .map((row) => ({
          taxCategory: row.taxCategory,
          rate: Math.max(0, parseFloat(String(row.rate).replace(',', '.')) || 0),
          customLabel: row.taxCategory === 'other' ? String(row.customLabel || '').trim() : null
        }))
        .filter((row) => {
          if (row.taxCategory === 'other') return (row.customLabel || '').length > 0;
          return true;
        }),
      usesSizeVariants: formData.usesSizeVariants,
      media
    };

    try {
      if (editingProduct) {
        await productService.updateProduct(editingProduct.id, payload);
        await fetchProducts();
        setEditingProduct(null);
      } else {
        await productService.createProduct(payload);
        await fetchProducts();
      }

      setFormData({
        name: '',
        description: '',
        category: DEFAULT_STORE_CATEGORY,
        season: '',
        launchDate: '',
        endDate: '',
        supplierCost: '',
        supplierShippingReference: '',
        price: '',
        totalQuantity: '',
        status: 'active',
        taxCategory: 'standard',
        ivaRate: 19,
        exemptReason: '',
        extraTaxes: [],
        fulfillment: 'javiland',
        weight: '500',
        lengthCm: '',
        widthCm: '',
        heightCm: '',
        packageType: 'sobre',
        usesSizeVariants: false,
        images: emptyImages(),
        variants: [
          { size: 'XS', stock: 0, price: 0 },
          { size: 'S', stock: 0, price: 0 },
          { size: 'M', stock: 0, price: 0 },
          { size: 'L', stock: 0, price: 0 },
          { size: 'XL', stock: 0, price: 0 }
        ]
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving product:', error);
      alert(error.response?.data?.message || 'Error al guardar el producto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const m = product.media && typeof product.media === 'object' ? product.media : {};
    const primaryImage = Array.isArray(product.images)
      ? product.images[0] || ''
      : product.images?.front || product.image || '';
    setFormData({
      name: product.name,
      description: product.description,
      category:
        product.category && String(product.category).toLowerCase() !== 'sale'
          ? product.category
          : DEFAULT_STORE_CATEGORY,
      season: product.season || '',
      launchDate: product.launchDate || '',
      endDate: product.endDate || '',
      supplierCost:
        product.supplierCost != null && product.supplierCost !== ''
          ? String(product.supplierCost)
          : '',
      supplierShippingReference:
        product.supplierShippingReferenceCop != null && product.supplierShippingReferenceCop !== ''
          ? String(product.supplierShippingReferenceCop)
          : '',
      price: product.price,
      totalQuantity: product.totalQuantity ?? product.stock ?? 0,
      status:
        ['active', 'inactive', 'upcoming', 'soldout'].includes(product.status) ? product.status : 'active',
      taxCategory: product.taxCategory || 'standard',
      ivaRate: product.ivaRate || 19,
      exemptReason: product.exemptReason || '',
      extraTaxes:
        Array.isArray(product.extraTaxes) && product.extraTaxes.length > 0
          ? product.extraTaxes.map((x) => ({
              taxCategory: x.taxCategory || 'consumption',
              rate: x.rate != null && x.rate !== '' ? String(x.rate) : String(defaultRateForTax(x.taxCategory || 'consumption')),
              customLabel: x.customLabel || ''
            }))
          : product.extraTaxName
            ? [
                {
                  taxCategory: 'other',
                  rate:
                    product.extraTaxRate != null && product.extraTaxRate !== ''
                      ? String(product.extraTaxRate)
                      : '',
                  customLabel: product.extraTaxName
                }
              ]
            : [],
      fulfillment: product.fulfillment === 'supplier' ? 'supplier' : 'javiland',
      weight:
        product.weightGrams != null
          ? String(product.weightGrams)
          : product.weight != null
            ? String(product.weight)
            : '',
      lengthCm:
        product.dimensionsCm?.length != null && product.dimensionsCm.length !== ''
          ? String(product.dimensionsCm.length)
          : '',
      widthCm:
        product.dimensionsCm?.width != null && product.dimensionsCm.width !== ''
          ? String(product.dimensionsCm.width)
          : '',
      heightCm:
        product.dimensionsCm?.height != null && product.dimensionsCm.height !== ''
          ? String(product.dimensionsCm.height)
          : '',
      packageType: product.packageType || 'sobre',
      usesSizeVariants: !!product.usesSizeVariants,
      images: {
        ...emptyImages(),
        front: primaryImage || m.front || '',
        back: m.back || '',
        left: m.left || '',
        right: m.right || '',
        diagonal: m.diagonal || '',
        detail: m.detail || (!Array.isArray(product.images) && product.images?.detail) || '',
        video1: m.video1 || '',
        video2: m.video2 || ''
      },
      variants: product.variants || [
        { size: 'XS', stock: 0, price: product.price || 0 },
        { size: 'S', stock: 0, price: product.price || 0 },
        { size: 'M', stock: 0, price: product.price || 0 },
        { size: 'L', stock: 0, price: product.price || 0 },
        { size: 'XL', stock: 0, price: product.price || 0 }
      ]
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('¿Desactivar este producto? (no se elimina, solo deja de mostrarse)')) {
      try {
        await productService.deleteProduct(productId);
        await fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert(error.response?.data?.message || 'Error al desactivar');
      }
    }
  };

  const getStatusColor = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.color : 'secondary';
  };

  const getStatusLabel = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.label : 'Desconocido';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="product-management">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Cargando productos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="product-management">
      <div className="container">
        <div className="section-header">
          <h2>Gestión de Productos</h2>
          <p>Administra el catálogo de productos Javiland</p>
        </div>

        {/* Botón para agregar producto */}
        <div className="actions-bar actions-bar--products">
          <input
            type="search"
            className="products-search-input"
            placeholder="Buscar por nombre, ID o categoría…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Buscar productos"
          />
          <label className="products-list-filter">
            <span>Estado:</span>
            <select
              value={listStatusFilter}
              onChange={(e) => setListStatusFilter(e.target.value)}
              aria-label="Filtrar productos por estado"
            >
              {listFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="add-product-btn"
            onClick={() => {
              setEditingProduct(null);
              setShowAddForm(true);
            }}
          >
            <i className="fas fa-plus"></i>
            Agregar producto
          </button>
        </div>

        {showAddForm && (
          <div className="product-form-container" ref={formPanelRef}>
            <div className="product-form">
              <div className="form-header">
                <h3>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</h3>
                <button 
                  className="close-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    setFormData({
                      name: '',
                      description: '',
                      category: DEFAULT_STORE_CATEGORY,
                      season: '',
                      launchDate: '',
                      endDate: '',
                      supplierCost: '',
                      price: '',
                      totalQuantity: '',
                      status: 'active',
                      taxCategory: 'standard',
                      ivaRate: 19,
                      exemptReason: '',
                      extraTaxes: [],
                      fulfillment: 'javiland',
                      weight: '500',
                      lengthCm: '',
                      widthCm: '',
                      heightCm: '',
                      packageType: 'sobre',
                      usesSizeVariants: false,
                      images: emptyImages(),
                      variants: [
                        { size: 'XS', stock: 0, price: 0 },
                        { size: 'S', stock: 0, price: 0 },
                        { size: 'M', stock: 0, price: 0 },
                        { size: 'L', stock: 0, price: 0 },
                        { size: 'XL', stock: 0, price: 0 }
                      ]
                    });
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Nombre del Producto *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Camiseta Javiland Verano 2024"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Descripción *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="Describe el producto, materiales, diseño..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">Categoría en la tienda *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {PRODUCT_CATEGORIES.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <small>
                    Elige la categoría real del producto. La vista <strong>Ofertas</strong> en la tienda agrupa descuentos de
                    todas las categorías; no se selecciona aquí.
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="launchDate">Fecha de lanzamiento</label>
                    <input
                      type="date"
                      id="launchDate"
                      name="launchDate"
                      value={formData.launchDate}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="endDate">Fecha de finalización</label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="season">Temporada (opcional)</label>
                  <select
                    id="season"
                    name="season"
                    value={formData.season}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar temporada</option>
                    {formData.season && !seasons.includes(formData.season) && (
                      <option value={formData.season}>{formData.season}</option>
                    )}
                    {seasons.map((season) => (
                      <option key={season} value={season}>
                        {season}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="supplierCost">Costo al proveedor (COP)</label>
                    <input
                      type="number"
                      id="supplierCost"
                      name="supplierCost"
                      value={formData.supplierCost}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                      placeholder="Opcional"
                    />
                    <small>Lo que pagaría Javiland (referencia interna; no es el precio que ve el cliente).</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="price">Precio de venta al público (COP) *</label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="100"
                      placeholder="5000"
                    />
                    <small>Precio mostrado en la tienda y base en checkout. El desglose IVA/otros impuestos en factura se refina en facturación.</small>
                  </div>

                  {!formData.usesSizeVariants && (
                    <div className="form-group">
                      <label htmlFor="totalQuantity">Cantidad en stock *</label>
                      <input
                        type="number"
                        id="totalQuantity"
                        name="totalQuantity"
                        value={formData.totalQuantity}
                        onChange={handleInputChange}
                        required
                        min="0"
                        placeholder="10"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="status">Estado *</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="tax-config-section">
                  <h4>⚖️ Configuración de impuestos</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="taxCategory">Categoría fiscal *</label>
                      <select
                        id="taxCategory"
                        name="taxCategory"
                        value={formData.taxCategory}
                        onChange={handleTaxCategoryChange}
                        required
                      >
                        <option value="standard">IVA 19% estándar</option>
                        <option value="reduced">IVA 5% reducido</option>
                        <option value="exempt">Exento de IVA</option>
                        <option value="consumption">Impuesto al consumo 8%</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="ivaRate">Tasa de IVA (%)</label>
                      <input
                        type="number"
                        id="ivaRate"
                        name="ivaRate"
                        value={formData.ivaRate}
                        onChange={handleInputChange}
                        min="0"
                        max="100"
                        step="1"
                        placeholder="19"
                      />
                    </div>
                  </div>

                  {formData.taxCategory === 'exempt' && (
                    <div className="form-group">
                      <label htmlFor="exemptReason">Razón de exención *</label>
                      <input
                        type="text"
                        id="exemptReason"
                        name="exemptReason"
                        value={formData.exemptReason}
                        onChange={handleInputChange}
                        placeholder="Ej: Producto de la canasta básica, según norma"
                      />
                    </div>
                  )}

                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <p className="form-hint" style={{ marginBottom: '0.5rem' }}>
                      ¿Deseas declarar otro impuesto para este producto? (máximo {MAX_EXTRA_PRODUCT_TAXES}, mismo tipo de lista que
                      arriba u “Otro”)
                    </p>
                    {formData.extraTaxes.map((row, idx) => (
                      <div
                        key={`extra-tax-${idx}`}
                        className="form-row"
                        style={{ alignItems: 'flex-end', marginBottom: '0.75rem', flexWrap: 'wrap' }}
                      >
                        <div className="form-group">
                          <label htmlFor={`extraTaxCat-${idx}`}>Impuesto adicional {idx + 1}</label>
                          <select
                            id={`extraTaxCat-${idx}`}
                            value={row.taxCategory}
                            onChange={(e) => updateExtraTaxRow(idx, 'taxCategory', e.target.value)}
                          >
                            {fiscalSelectOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor={`extraTaxRate-${idx}`}>Tasa (%)</label>
                          <input
                            id={`extraTaxRate-${idx}`}
                            type="number"
                            value={row.rate}
                            onChange={(e) => updateExtraTaxRow(idx, 'rate', e.target.value)}
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                        {row.taxCategory === 'other' && (
                          <div className="form-group" style={{ flex: '1 1 200px' }}>
                            <label htmlFor={`extraTaxLabel-${idx}`}>Nombre del impuesto *</label>
                            <input
                              id={`extraTaxLabel-${idx}`}
                              type="text"
                              value={row.customLabel}
                              onChange={(e) => updateExtraTaxRow(idx, 'customLabel', e.target.value)}
                              placeholder="Nombre para declaración"
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          className="cancel-btn"
                          style={{ marginBottom: '4px' }}
                          onClick={() => removeExtraTaxRow(idx)}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                    {formData.extraTaxes.length < MAX_EXTRA_PRODUCT_TAXES && (
                      <button type="button" className="add-product-btn" onClick={addExtraTaxRow}>
                        + Añadir otro impuesto
                      </button>
                    )}
                  </div>
                </div>

                <div className="shipping-config-section">
                  <h4>🚚 Envío y cumplimiento</h4>
                  <p className="form-hint shipping-hint">
                    Origen de despacho: <strong>Chía, Cundinamarca</strong>. Javiland: Chía domicilio gratis; ciudades principales tarifa fija;
                    resto del país cotización Envia por línea. El total de envío al cliente es el <strong>promedio</strong> de las
                    líneas con costo &gt; 0. Si envía el proveedor, indica el costo de envío que te cobra (referencia para el cálculo).
                  </p>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fulfillment">¿Quién envía al cliente? *</label>
                      <select
                        id="fulfillment"
                        name="fulfillment"
                        value={formData.fulfillment}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="javiland">Javiland (almacén / paquetería)</option>
                        <option value="supplier">Proveedor (dropship / envío directo)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="weight">Peso por unidad (g) *</label>
                      <input
                        type="number"
                        id="weight"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        required
                        min="1"
                        step="1"
                        placeholder="500"
                      />
                      <small>
                        {formData.fulfillment === 'supplier'
                          ? 'Referencia interna; no suma envío Javiland en el checkout.'
                          : 'Usado para cotizar envío (Javiland).'}
                      </small>
                    </div>

                    {formData.fulfillment === 'supplier' && (
                      <div className="form-group">
                        <label htmlFor="supplierShippingReference">Costo de envío que cobra el proveedor (COP)</label>
                        <input
                          type="number"
                          id="supplierShippingReference"
                          name="supplierShippingReference"
                          value={formData.supplierShippingReference}
                          onChange={handleInputChange}
                          min="0"
                          step="100"
                          placeholder="Ej: 8000"
                        />
                        <small>Referencia para el promedio de envío en checkout (con líneas Javiland si las hay).</small>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="packageType">Tipo de empaque *</label>
                      <select
                        id="packageType"
                        name="packageType"
                        value={formData.packageType}
                        onChange={handleInputChange}
                        required
                      >
                        {Object.entries(PACKAGE_TYPE_DIMENSIONS_CM).map(([key, spec]) => (
                          <option key={key} value={key}>
                            {spec.label}
                          </option>
                        ))}
                      </select>
                      <small>
                        Medidas alineadas con la cotización del backend (Envia). Puedes rellenar L×A×H manualmente abajo o usar el
                        botón para copiar las del tipo elegido.
                      </small>
                    </div>
                  </div>

                  <div className="form-row">
                    <button type="button" className="cancel-btn" onClick={applyStandardPackageDimensions}>
                      Aplicar medidas estándar del tipo de empaque (cm)
                    </button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="lengthCm">Largo del paquete (cm) *</label>
                      <input
                        type="number"
                        id="lengthCm"
                        name="lengthCm"
                        value={formData.lengthCm}
                        onChange={handleInputChange}
                        min="0"
                        step="0.1"
                        placeholder="25"
                        required={formData.fulfillment === 'javiland'}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="widthCm">Ancho (cm) *</label>
                      <input
                        type="number"
                        id="widthCm"
                        name="widthCm"
                        value={formData.widthCm}
                        onChange={handleInputChange}
                        min="0"
                        step="0.1"
                        placeholder="18"
                        required={formData.fulfillment === 'javiland'}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="heightCm">Alto (cm) *</label>
                      <input
                        type="number"
                        id="heightCm"
                        name="heightCm"
                        value={formData.heightCm}
                        onChange={handleInputChange}
                        min="0"
                        step="0.1"
                        placeholder="3"
                        required={formData.fulfillment === 'javiland'}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={`variants-section ${formData.usesSizeVariants ? 'variants-section--on' : 'variants-section--off'}`}
                >
                  <div className="variants-section-header">
                    <h4>Inventario por talla/tamaño</h4>
                    <span
                      className={`variant-mode-badge ${formData.usesSizeVariants ? 'variant-mode-badge--on' : 'variant-mode-badge--off'}`}
                      aria-live="polite"
                    >
                      {formData.usesSizeVariants ? 'Activado' : 'Desactivado'}
                    </span>
                  </div>
                  <div className="form-group variant-toggle-wrap">
                    <label className="checkbox-label variant-toggle-label">
                      <input
                        type="checkbox"
                        name="usesSizeVariants"
                        checked={formData.usesSizeVariants}
                        onChange={handleInputChange}
                      />
                      <span>¿El producto tiene diferentes tallas o tamaños?</span>
                    </label>
                    <small>
                      {formData.usesSizeVariants
                        ? 'Modo activo: el stock total es la suma por talla. Completa cada talla abajo.'
                        : 'Modo inactivo: usa un solo campo «Cantidad en stock» arriba.'}{' '}
                      Activa solo para ropa, tallas de instrumentos, etc.
                    </small>
                  </div>

                  {formData.usesSizeVariants && (
                    <div className="variants-grid">
                      {formData.variants.map((variant, index) => (
                        <div key={variant.size} className="variant-item">
                          <label className="variant-size">{variant.size}</label>
                          <input
                            type="number"
                            placeholder="Stock"
                            value={variant.stock}
                            onChange={(e) => handleVariantChange(index, 'stock', parseInt(e.target.value, 10) || 0)}
                            min="0"
                          />
                          <span className="variant-price">
                            {formData.price ? formatPrice(Number(formData.price)) : 'COP 0'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="images-section">
                  <h4>Imágenes y videos (URLs)</h4>
                  <p className="form-hint">
                    Suelen ser rutas públicas en tu hosting (carpeta <code>public_html</code>/<code>images</code>) o CDN. La
                    principal para listados es la frontal; se guarda como imagen principal del producto.
                  </p>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="frontImage">Imagen principal (frontal) *</label>
                      <input
                        type="text"
                        id="frontImage"
                        name="frontImage"
                        value={formData.images.front}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, front: e.target.value }
                          }))
                        }
                        placeholder="/images/tienda/… o https://javiland.com/images/…"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="backImage">Trasera</label>
                      <input
                        type="text"
                        id="backImage"
                        value={formData.images.back}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, back: e.target.value }
                          }))
                        }
                        placeholder="URL"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="leftImage">Lateral izquierda</label>
                      <input
                        type="text"
                        id="leftImage"
                        value={formData.images.left}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, left: e.target.value }
                          }))
                        }
                        placeholder="URL"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="rightImage">Lateral derecha</label>
                      <input
                        type="text"
                        id="rightImage"
                        value={formData.images.right}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, right: e.target.value }
                          }))
                        }
                        placeholder="URL"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="diagImage">Diagonal / 45°</label>
                      <input
                        type="text"
                        id="diagImage"
                        value={formData.images.diagonal}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, diagonal: e.target.value }
                          }))
                        }
                        placeholder="URL"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="detailImage">Detalle extra</label>
                      <input
                        type="text"
                        id="detailImage"
                        value={formData.images.detail}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, detail: e.target.value }
                          }))
                        }
                        placeholder="URL"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="video1">Video 1 (URL)</label>
                      <input
                        type="url"
                        id="video1"
                        value={formData.images.video1}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, video1: e.target.value }
                          }))
                        }
                        placeholder="YouTube, Vimeo o archivo .mp4 público"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="video2">Video 2 (URL)</label>
                      <input
                        type="url"
                        id="video2"
                        value={formData.images.video2}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            images: { ...prev.images, video2: e.target.value }
                          }))
                        }
                        placeholder="URL"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                  }}>
                    Cancelar
                  </button>
                  <button type="submit" className="save-btn">
                    <i className="fas fa-save"></i>
                    {editingProduct ? 'Actualizar' : 'Crear'} Producto
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="products-list">
          <h3>
            Catálogo ({displayedProducts.length}
            {searchTerm ? ` de ${products.length}` : ''}) — Bodega Chía primero
          </h3>

          {displayedProducts.length === 0 ? (
            <div className="no-products">
              <i className="fas fa-tshirt"></i>
              <p>{products.length === 0 ? 'No hay productos registrados' : 'Sin coincidencias en la búsqueda'}</p>
            </div>
          ) : (
            <div className="products-table-wrap">
              <table className="products-admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Producto</th>
                    <th>Estado</th>
                    <th>Despacho</th>
                    <th>Stock</th>
                    <th>Precio</th>
                    <th>Categoría</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {displayedProducts.map((product) => {
                    const stock = product.variants?.length
                      ? product.variants.reduce((s, v) => s + (Number(v.stock) || 0), 0)
                      : Number(product.stock ?? product.totalQuantity ?? 0);
                    const img =
                      product.image || product.images?.front || '/images/placeholders/placeholder.svg';
                    return (
                      <tr key={product.id}>
                        <td className="products-td-id">{product.id}</td>
                        <td className="products-td-name">
                          <img src={img} alt="" className="products-td-thumb" />
                          <span>{product.name}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusColor(product.status)}`}>
                            {getStatusLabel(product.status)}
                          </span>
                        </td>
                        <td>{fulfillmentLabel(product.fulfillment)}</td>
                        <td className={stock === 0 ? 'products-stock-zero' : ''}>{stock}</td>
                        <td>{formatPrice(product.price)}</td>
                        <td>{product.category || '—'}</td>
                        <td className="products-td-actions">
                          <button type="button" className="edit-btn" onClick={() => handleEdit(product)} title="Editar">
                            <i className="fas fa-edit" />
                          </button>
                          <button type="button" className="delete-btn" onClick={() => handleDelete(product.id)} title="Desactivar">
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductManagement;






















